/**
 * The strength level of an account's second-factor setup — PRD-M13-R2.
 *
 * A LEVEL, not a number: nothing computes a score, so showing "92" would be a
 * figure with no inputs. The rule, in one place:
 *
 * - Only a factor that sign-in checks (authenticator app, phone) counts as a
 *   base. Recovery codes and email codes are backups the backend uses for
 *   step-up; with no sign-in factor they protect nothing, so they alone are
 *   `none`. That is R2's "backup codes alone are not strong".
 * - An authenticator app is the stronger base; SMS alone is `fair`.
 * - A fallback — a second factor, email codes, or an unspent recovery code —
 *   lifts the base one level. Strong needs an authenticator app plus one.
 */
export type StrengthInput = {
  totp: boolean
  phone: boolean
  email: boolean
  recoveryRemaining: number
}

export type Strength = 'none' | 'fair' | 'good' | 'strong'

export const STRENGTH_LABEL: Record<Strength, string> = {
  none: 'Password only',
  fair: 'Basic',
  good: 'Good',
  strong: 'Strong',
}

export function securityStrength({ totp, phone, email, recoveryRemaining }: StrengthInput): Strength {
  const backup = email || recoveryRemaining > 0
  if (totp) return phone || backup ? 'strong' : 'good'
  if (phone) return backup ? 'good' : 'fair'
  return 'none'
}
